import React from 'react';
import { ArrowRightIcon, CheckIcon, XIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { easeEnter, revealTransition, stagger, viewportOnce } from '../utils/motion';
const oldModel = ['Spend on distribution', 'Hand out product', 'Wait for a result'];
const newModel = ['Target a moment', 'Activate participation', 'Capture trial', 'Learn from signals', 'Optimize what happens next'];
export function Problem() {
  const reduceMotion = useReducedMotion();
  return <section className="bg-ink px-5 py-24 text-paper md:px-10 md:py-32" aria-labelledby="model-heading" data-id="element-625" data-mp="26hei1xnr4267">
      <div className="mx-auto max-w-shell" data-id="element-626" data-mp="1sn55n9sur5tl">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={viewportOnce} transition={revealTransition} className="max-w-4xl" data-id="element-627" data-mp="1wqbgkuqwtl6e">
          <p className="text-xs font-extrabold tracking-[0.16em] text-paper/50" data-id="element-628" data-mp="ob3ye4o5ntfc">
            THE CATEGORY SHIFT
          </p>
          <h2 id="model-heading" className="mt-5 font-display text-4xl font-bold leading-[0.9] md:text-7xl" data-id="element-629" data-mp="129k884hbe01p">
            Sampling was built to distribute.
            <span className="text-paper/45" data-id="element-630" data-mp="9l69tnmbd1pm"> Sampler is built to learn.</span>
          </h2>
        </motion.div>

        <div className="mt-16 grid gap-5 lg:grid-cols-[0.75fr_auto_1.45fr] lg:items-stretch" data-id="element-631" data-mp="5lnmymaxmgk5">
          <motion.article initial={reduceMotion ? false : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={viewportOnce} transition={revealTransition} className="flex flex-col border border-paper/20 p-7 md:p-9" data-id="element-632" data-mp="jcsoxv1rvkxn">
            <p className="text-xs font-extrabold tracking-[0.14em] text-paper/45" data-id="element-633" data-mp="1qxfxgw4sm38u">
              THE OLD MODEL
            </p>
            <h3 className="mt-4 font-display text-3xl font-bold text-paper/70" data-id="element-634" data-mp="1vo656g0oyi6p">
              Spend → distribute → hope.
            </h3>
            <ul className="mt-10 space-y-5" data-id="element-635" data-mp="20mpnm6g2tbub">
              {oldModel.map(item => <li key={item} className="flex items-center gap-3 font-bold text-paper/55" data-id="element-636" data-mp="131bkpj3vjit8">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-paper/25" data-id="element-637" data-mp="1cy6hnxki1b5e">
                    <XIcon size={14} aria-hidden="true" data-id="element-638" data-mp="1h01jr4a97sk8" />
                  </span>
                  {item}
                </li>)}
            </ul>
            <p className="mt-auto border-t border-paper/15 pt-6 text-sm leading-relaxed text-paper/55" data-id="element-639" data-mp="2of6bixmhvy1j">
              A physical moment disappears without a measurable relationship
              behind it.
            </p>
          </motion.article>

          <div className="hidden items-center justify-center lg:flex" aria-hidden="true" data-id="element-640" data-mp="cphz7k3w1tn2">
            <ArrowRightIcon size={26} className="text-signal" data-id="element-641" data-mp="7xgpa4u70f07" />
          </div>

          <motion.article initial={reduceMotion ? false : {
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={viewportOnce} transition={{
          ...revealTransition,
          delay: stagger(2, reduceMotion)
        }} className="flex flex-col bg-paper p-7 text-ink md:p-9" data-id="element-642" data-mp="2yvpu7mrlzct">
            <p className="text-xs font-extrabold tracking-[0.14em] text-signal" data-id="element-643" data-mp="1jc3xagnb87e0">
              THE MEASURABLE MEDIA MODEL
            </p>
            <h3 className="mt-4 font-display text-3xl font-bold leading-[0.95] md:text-4xl" data-id="element-644" data-mp="b3oe8uf7twbu">
              Target → activate → trial → learn → optimize.
            </h3>
            <div className="mt-10 flex flex-wrap gap-2" data-id="element-645" data-mp="g0wvmssjhwc1">
              {newModel.map((item, index) => <motion.span key={item} initial={reduceMotion ? false : {
              opacity: 0,
              y: 8
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={viewportOnce} transition={{
              duration: 0.28,
              ease: easeEnter,
              delay: stagger(index, reduceMotion)
            }} className="flex items-center gap-2.5 border border-ink/20 px-3 py-2 text-sm font-bold" data-id="element-646" data-mp="72kuhwu1rg5c" data-mpi={index}>
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-ink text-[10px] font-extrabold text-paper" data-id="element-647" data-mp="2qyb2gvaj8rxq">
                    {index + 1}
                  </span>
                  {item}
                </motion.span>)}
            </div>
            <div className="mt-auto flex gap-3 border-t border-ink/15 pt-6" data-id="element-648" data-mp="2vwcu44m0xzkt">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-signal text-white" data-id="element-649" data-mp="39wdr38z64mv0">
                <CheckIcon size={15} aria-hidden="true" data-id="element-650" data-mp="34xki9tuttk16" />
              </span>
              <p className="text-sm leading-relaxed text-ink/75" data-id="element-651" data-mp="34vgav1xpy2w5">
                Every product experience becomes an addressable media moment —
                and every moment returns evidence your brand can act on.
              </p>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}